// Chart type constants
export const CHART_TYPES = {
  SEVERITY: "severity",
  AUTO_RESOLVED: "autoResolved",
  TRIGGERED_FAULT_BREAKDOWN: "triggeredFaultBreakdown",
  RESOLUTION_DURATION_BREAKDOWN: "resolutionDurationBreakdown",
  MOST_CRITICAL_VEHICLES: "mostCriticalVehicles",
};

// Chart configuration for different chart types
export const CHART_CONFIG = {
  [CHART_TYPES.SEVERITY]: {
    title: "Severity of Faults Triggered vs Vehicles Impacted",
    metaInfo: [
      "licensePlateNumber",
      "originCountry",
      "originState",
      "originCity",
      "originDepo",
      "pingStatus",
    ],
    tableColumnOrder: [
      "licensePlateNumber",
      "critical",
      "high severity",
      "warning",
      "originCountry",
      "originState",
      "originCity",
      "originDepo",
    ],
    tableColumnsConfig: {
      licensePlateNumber: {
        title: "Bus Number",
        headerLabel: "Bus Number",
        dataIndex: "licensePlateNumber",
        key: "licensePlateNumber",
        width: "auto",
        valueRenderingType: "device-id-link",
        fixed: "left",
      },
      critical: {
        title: "Critical Faults",
        headerLabel: "Critical Faults",
        dataIndex: "critical",
        key: "critical",
        valueRenderingType: "number",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
        width: 150,
      },
      "high severity": {
        title: "High Severity Faults",
        headerLabel: "High Severity Faults",
        dataIndex: "high severity",
        key: "high severity",
        textFormat: "title",
        valueRenderingType: "number",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
        width: 180,
      },
      warning: {
        title: "Warnings",
        headerLabel: "Warnings",
        dataIndex: "warning",
        key: "warning",
        width: 120,
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
        valueRenderingType: "number",
      },
      originCountry: {
        title: "Country",
        headerLabel: "Country",
        dataIndex: "originCountry",
        key: "originCountry",
        textFormat: "title",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
        width: 150,
      },
      originState: {
        title: "State",
        headerLabel: "State",
        dataIndex: "originState",
        key: "originState",
        textFormat: "title",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
        width: 150,
      },
      originCity: {
        title: "City",
        headerLabel: "City",
        dataIndex: "originCity",
        key: "originCity",
        textFormat: "title",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
        width: 150,
      },
      originDepo: {
        title: "Depot",
        headerLabel: "Depot",
        dataIndex: "originDepo",
        key: "originDepo",
        textFormat: "title",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
        width: 200,
      },
    },
  
    popupConfig: {
      getPopupTitle: (selectedDataPoint) => {
        const severityName = selectedDataPoint?.metricName || "Faults";
        const category = selectedDataPoint?.category || "";
        return `Drill Down: Severity of Faults Triggered vs Vehicles Impacted / ${severityName} • ${category}`;
      },
      summaryTable: {
        title: (selectedDataPoint) => {
          const severityName = selectedDataPoint?.metricName || "Alerts";
          return `${severityName} Alerts`;
        },
        tableColumnsConfig: {
          xLabel: {
            title: "Fault Name",
            dataIndex: "xLabel",
            key: "xLabel",
            width: "320px",
            fixed: "left",
            defaultValue: "Not Set",
            sorter: true,
            sortKey: "errorDescription",
            sortDirections: ["ascend", "descend", "ascend"],
          },
          "1": {
            title: "Warning (L1) Faults ",
            dataIndex: "1",
            key: "1",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },

          "2": {
            title: "Warning (L2) Faults ",
            dataIndex: "2",
            key: "2",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },

          "3": {
            title: "High Severity (L3) Faults ",
            dataIndex: "3",
            key: "3",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          "4": {
            title: "High Severity (L4) Faults ",
            dataIndex: "4",
            key: "4",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          "5": {
            title: "Critical (L5) Faults ",
            dataIndex: "5",
            key: "5",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          vehicleCount: {
            title: "Vehicles Affected",
            dataIndex: "vehicleCount",
            key: "vehicleCount",
            width: 100,
            valueRenderingType: "number",
          },
          actions: {
            title: "Actions",
            headerLabel: "Actions",
            dataIndex: "actions",
            key: "actions",
            width: 120,
            fixed: "right",
            valueRenderingType: "details-link",
          },
        },
        tableColumnOrder: ["xLabel", "1", "2", "3", "4", "5", "vehicleCount", "actions"],

        transformData: (apiData, selectedDataPoint, vehiclesData) => {
          const severityType = (selectedDataPoint?.metricName || "").toLowerCase();
          
          // Create a map of vehicles affected by fault name (xLabel)
          // Since we group by ["errorDescription", "FaultSeverityName"], xLabel should match
          const vehiclesAffectedMap = new Map();
          if (vehiclesData && Array.isArray(vehiclesData)) {
            console.log("Vehicles data for mapping:", vehiclesData);
            vehiclesData.forEach((item) => {
              if (item.xLabel) {
                let vehiclesAffected = 0;
                // For VehiclesAffectedCountForGeneratedFaults, the value might be directly in metricsArray
                // or the metric type might be different
                if (item.metricsArray && Array.isArray(item.metricsArray)) {
                  item.metricsArray.forEach((metric) => {
                    const metricType = (metric.type || "").toLowerCase();
                    // Try multiple possible metric type names
                    if (metricType === "vehicles" || metricType === "vehicle" || metricType === "vehiclesaffected" || metricType === "vehicles affected") {
                      vehiclesAffected = metric.value || 0;
                    }
                    // If no specific type matches, use the first metric value (some APIs might not have a type)
                    if (vehiclesAffected === 0 && item.metricsArray.length === 1) {
                      vehiclesAffected = metric.value || 0;
                    }
                  });
                }
                console.log(`Mapping xLabel: ${item.xLabel} -> vehiclesAffected: ${vehiclesAffected}`);
                vehiclesAffectedMap.set(item.xLabel, vehiclesAffected);
              }
            });
          }
          
          console.log("Vehicles affected map:", Array.from(vehiclesAffectedMap.entries()));

          return apiData.map((item, index) => {
            const row = {
              id: `fault-${index}`,
              faultName: item.xLabel || "",
              vehiclesAffected: 0,
              faultCount: 0,
            };

            if (item.metricsArray && Array.isArray(item.metricsArray)) {
              item.metricsArray.forEach((metric) => {
                const metricType = (metric.type || "").toLowerCase();
                if (
                  (severityType === "critical" || severityType === "crit") &&
                  (metricType === "critical" || metricType === "crit")
                ) {
                  row.faultCount = metric.value || 0;
                } else if (
                  (severityType === "high severity" || severityType === "high") &&
                  (metricType === "high severity" || metricType === "high" || metricType === "highseverity")
                ) {
                  row.faultCount = metric.value || 0;
                } else if (
                  (severityType === "warning" || severityType === "warn") &&
                  (metricType === "warning" || metricType === "warn")
                ) {
                  row.faultCount = metric.value || 0;
                }
              });
            }

            // Get vehicles affected from the separate API call, fallback to metaInfo, then faultCount
            const vehiclesFromMap = vehiclesAffectedMap.get(item.xLabel);
            console.log(`For xLabel: ${item.xLabel}, vehiclesFromMap: ${vehiclesFromMap}, metaInfo: ${item.metaInfo?.vehiclesAffected}, faultCount: ${row.faultCount}`);
            row.vehiclesAffected = vehiclesFromMap !== undefined ? vehiclesFromMap : (item.metaInfo?.vehiclesAffected !== undefined ? item.metaInfo.vehiclesAffected : row.faultCount);

            return row;
          });
        },
      },
      vehiclesTable: {
        title: "Vehicles Affected",
        tableColumnsConfig: {
          licensePlateNumber: {
            title: "Bus Number",
            headerLabel: "Bus Number",
            dataIndex: "licensePlateNumber",
            key: "licensePlateNumber",
            width: 150,
            valueRenderingType: "device-id-link",
            fixed: "left",
          },
          "1": {
            title: "Warning (L1) Faults ",
            dataIndex: "1",
            key: "1",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },

          "2": {
            title: "Warning (L2) Faults ",
            dataIndex: "2",
            key: "2",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },

          "3": {
            title: "High Severity (L3) Faults ",
            dataIndex: "3",
            key: "3",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          "4": {
            title: "High Severity (L4) Faults ",
            dataIndex: "4",
            key: "4",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          "5": {
            title: "Critical (L5) Faults ",
            dataIndex: "5",
            key: "5",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          originState: {
            title: "State",
            headerLabel: "State",
            dataIndex: "originState",
            key: "originState",
            width: 150,
            textFormat: "title",
            sortDirections: ["ascend", "descend", "ascend"],
            sorter: true,
          },
          originCity: {
            title: "City",
            headerLabel: "City",
            dataIndex: "originCity",
            key: "originCity",
            width: 150,
            textFormat: "title",
            sortDirections: ["ascend", "descend", "ascend"],
            sorter: true,
          },
          originDepo: {
            title: "Depot",
            headerLabel: "Depot",
            dataIndex: "originDepo",
            key: "originDepo",
            width: 200,
            textFormat: "title",
            sortDirections: ["ascend", "descend", "ascend"],
            sorter: true,
          },
        },
        tableColumnOrder: ["licensePlateNumber", "1", "2", "3", "4", "5", "originState", "originCity", "originDepo"],
        apiSelect: "GeneratedFaultsCount",
        apiGroupBy: ["batteryID", "FaultSeverityLevelID"],
        getApiFilters: (selectedFaultRow, selectedDataPoint, baseFilters) => {
          const filters = [...baseFilters];
          if (selectedFaultRow?.faultName) {
            filters.push({
              name: "errorDescription",
              values: [selectedFaultRow.faultName],
            });
          }
          if (selectedDataPoint?.metricName) {
            filters.push({
              name: "FaultSeverityName",
              values: [selectedDataPoint.metricName],
            });
          }
          return filters;
        },
        transformData: (apiData, selectedFaultRow, selectedDataPoint) => {
          // For vehicles table, we group by batteryID, so the API returns GeneratedFaultsCount
          // The severity is already filtered at the API level, so we just need to extract the count
          return apiData.map((item) => {
            const row = {
              id: item.xLabel || "",
              licensePlateNumber: item.xLabel || "",
              faultCount: 0,
              ...item.metaInfo,
            };

            // Extract fault count from metricsArray
            // When grouping by batteryID, the metric type is "GeneratedFaultsCount"
            if (item.metricsArray && Array.isArray(item.metricsArray)) {
              item.metricsArray.forEach((metric) => {
                const metricType = (metric.type || "").toLowerCase();
                // For vehicles table, the API returns GeneratedFaultsCount directly
                // since severity is already filtered in the API call
                if (metricType === "generatedfaultscount" || metricType === "generated faults count") {
                  row.faultCount = metric.value || 0;
                }
              });
            }

            return row;
          });
        },
        metaInfo: [
          "licensePlateNumber",
          "originCountry",
          "originState",
          "originCity",
          "originDepo",
          "pingStatus",
        ],
      },
    },
  },
  [CHART_TYPES.AUTO_RESOLVED]: {
    title: "Fault Resolution Trend (Auto vs Manual)",
    chartComponent: "AutoResolvedChartView",
    metaInfo: [
      "licensePlateNumber",
      "originCountry",
      "originState",
      "originCity",
      "originDepo",
      "pingStatus",
    ],
    tableColumnOrder: [
      "licensePlateNumber",
      "autoresolved",
      "manualresolved",
      "originCountry",
      "originState",
      "originCity",
      "originDepo",
    ],
    tableColumnsConfig: {
      licensePlateNumber: {
        title: "Bus Number",
        headerLabel: "Bus Number",
        dataIndex: "licensePlateNumber",
        key: "licensePlateNumber",
        width: "auto",
        fixed: "left",
        valueRenderingType: "device-id-link",
      },
      autoresolved: {
        title: "Auto Resolved",
        dataIndex: "autoresolved",
        key: "autoresolved",
        valueRenderingType: "number",
        width: 150,
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
      },
      manualresolved: {
        title: "Manually Resolved",
        dataIndex: "manualresolved",
        key: "manualresolved",
        valueRenderingType: "number",
        width: 180,
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
      },
      originCountry: {
        title: "Country",
        headerLabel: "Country",
        dataIndex: "originCountry",
        key: "originCountry",
        textFormat: "title",
        width: 150,
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
      },
      originState: {
        title: "State",
        headerLabel: "State",
        dataIndex: "originState",
        key: "originState",
        textFormat: "title",
        width: 150,
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
      },
      originCity: {
        title: "City",
        headerLabel: "City",
        dataIndex: "originCity",
        key: "originCity",
        textFormat: "title",
        width: 150,
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
      },
      originDepo: {
        title: "Depot",
        headerLabel: "Depot",
        dataIndex: "originDepo",
        key: "originDepo",
        textFormat: "title",
        width: 200,
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
      },
    },
    popupConfig: {
      getPopupTitle: (selectedDataPoint) => {
        const resolutionType = selectedDataPoint?.metricName || "Faults";
        const category = selectedDataPoint?.category || "";
        return `Drill Down: Fault Resolution Trend (Auto vs Manual) / ${resolutionType} • ${category}`;
      },
      summaryTable: {
        title: (selectedDataPoint) => {
          const resolutionType = selectedDataPoint?.metricName || "Resolved";
          return `${resolutionType} Alerts`;
        },
        tableColumnsConfig: {
          xLabel: {
            title: "Fault Name",
            headerLabel: "Fault Name",
            dataIndex: "xLabel",
            key: "xLabel",
            defaultValue: "Not Set",
            width: "auto",
            fixed: "left",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            sortKey: "errorDescription",
          },
          ResolvedFaultCount: {
            title: "Fault Count",
            dataIndex: "ResolvedFaultCount",
            key: "ResolvedFaultCount",
            width: 180,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
            
          },
          avgResolutionDuration:{
            title: "Avg. Resolution Duration",
            dataIndex: "avgResolutionDuration",
            key: "avgResolutionDuration",
            width: 180,
            valueRenderingType: "deltaTime 2",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          minResolutionDuration: {
            title: "Min. Resolution Duration",
            dataIndex: "minResolutionDuration",
            key: "minResolutionDuration",
            width: 180,
            valueRenderingType: "deltaTime 2",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          maxResolutionDuration: {
            title: "Max. Resolution Duration",
            dataIndex: "maxResolutionDuration",
            key: "maxResolutionDuration",
            width: 180,
            valueRenderingType: "deltaTime 2",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          actions: {
            title: "Actions",
            headerLabel: "Actions",
            dataIndex: "actions",
            key: "actions",
            width: 120,
            fixed: "right",
            valueRenderingType: "details-link",
          },
        },
        tableColumnOrder: ["xLabel", "ResolvedFaultCount", "avgResolutionDuration", "minResolutionDuration", "maxResolutionDuration", "actions"],
        apiSelect: "ResolvedFaultCount",
        apiGroupBy: ["errorDescription", "ResolutionType"],
        getApiFilters: (selectedDataPoint, baseFilters) => {
          const filters = [...baseFilters];
          if (selectedDataPoint?.metricName) {
            const resolutionType =
              selectedDataPoint.metricName === "Auto Resolved"
                ? "AutoResolved"
                : "ManualResolved";
            filters.push({
              name: "ResolutionType",
              values: [resolutionType],
            });
          }
          return filters;
        },
        transformData: (apiData, selectedDataPoint, vehiclesData) => {
          const resolutionType = (selectedDataPoint?.metricName || "").toLowerCase();
          
          // Create a map of vehicles affected by fault name (xLabel)
          // Since we group by ["errorDescription", "ResolutionType"], xLabel should match
          const vehiclesAffectedMap = new Map();
          if (vehiclesData && Array.isArray(vehiclesData)) {
            console.log("AUTO_RESOLVED - Vehicles data for mapping:", vehiclesData);
            vehiclesData.forEach((item) => {
              if (item.xLabel) {
                let vehiclesAffected = 0;
                // For VehiclesAffectedCountForResolvedFaults, the value might be directly in metricsArray
                // or the metric type might be different
                if (item.metricsArray && Array.isArray(item.metricsArray)) {
                  // Always use the first metric value if there's only one metric
                  // The API might return the count directly without a specific type
                  if (item.metricsArray.length === 1) {
                    vehiclesAffected = item.metricsArray[0].value || 0;
                  } else {
                    // If multiple metrics, try to find the vehicles metric
                    item.metricsArray.forEach((metric) => {
                      const metricType = (metric.type || "").toLowerCase();
                      // Try multiple possible metric type names
                      if (metricType === "vehicles" || metricType === "vehicle" || metricType === "vehiclesaffected" || metricType === "vehicles affected") {
                        vehiclesAffected = metric.value || 0;
                      }
                    });
                  }
                }
                console.log(`AUTO_RESOLVED - Mapping xLabel: ${item.xLabel} -> vehiclesAffected: ${vehiclesAffected}`);
                vehiclesAffectedMap.set(item.xLabel, vehiclesAffected);
              }
            });
          }
          console.log("AUTO_RESOLVED - Vehicles affected map:", Array.from(vehiclesAffectedMap.entries()));

          return apiData.map((item, index) => {
            const row = {
              id: `fault-${index}`,
              faultName: item.xLabel || "",
              vehiclesAffected: 0,
              faultCount: 0,
            };

            if (item.metricsArray && Array.isArray(item.metricsArray)) {
              item.metricsArray.forEach((metric) => {
                const metricType = (metric.type || "").toLowerCase();
                if (
                  (resolutionType === "auto resolved" || resolutionType === "auto") &&
                  (metricType === "autoresolved" || metricType === "auto-resolved")
                ) {
                  row.faultCount = metric.value || 0;
                } else if (
                  (resolutionType === "manually resolved" || resolutionType === "manual") &&
                  (metricType === "manualresolved" ||
                    metricType === "manual-resolved" ||
                    metricType === "manually resolved")
                ) {
                  row.faultCount = metric.value || 0;
                }
              });
            }

            // Get vehicles affected from the separate API call, fallback to metaInfo, then faultCount
            const vehiclesFromMap = vehiclesAffectedMap.get(item.xLabel);
            console.log(`AUTO_RESOLVED - For xLabel: ${item.xLabel}, vehiclesFromMap: ${vehiclesFromMap}, metaInfo: ${item.metaInfo?.vehiclesAffected}, faultCount: ${row.faultCount}`);
            row.vehiclesAffected = vehiclesFromMap !== undefined ? vehiclesFromMap : (item.metaInfo?.vehiclesAffected !== undefined ? item.metaInfo.vehiclesAffected : row.faultCount);

            return row;
          });
        },
      },
      vehiclesTable: {
        title: "Vehicles Affected",
        tableColumnsConfig: {
          licensePlateNumber: {
            title: "Bus Number",
            dataIndex: "licensePlateNumber",
            key: "licensePlateNumber",
            width: 150,
            valueRenderingType: "device-id-link",
            fixed: "left",
          },
          ResolvedFaultCount: {
            title: "Fault Resolved",
            dataIndex: "ResolvedFaultCount",
            key: "ResolvedFaultCount",
            width: 220,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          originState: {
            title: "State",
            headerLabel: "State",
            dataIndex: "originState",
            key: "originState",
            width: 150,
            textFormat: "title",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],

          },
          originCity: {
            title: "City",
            headerLabel: "City",
            dataIndex: "originCity",
            key: "originCity",
            width: 150,
            textFormat: "title",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],

          },
          originDepo: {
            title: "Depot",
            headerLabel: "Depot",
            dataIndex: "originDepo",
            key: "originDepo",
            width: 200,
            textFormat: "title",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
        },
        tableColumnOrder: ["licensePlateNumber", "ResolvedFaultCount", "originState", "originCity", "originDepo"],
        apiSelect: "ResolvedFaultCount",
        apiGroupBy: ["batteryID"],
        getApiFilters: (selectedFaultRow, selectedDataPoint, baseFilters) => {
          const filters = [...baseFilters];
          if (selectedFaultRow?.faultName) {
            filters.push({
              name: "errorDescription",
              values: [selectedFaultRow.faultName],
            });
          }
          if (selectedDataPoint?.metricName) {
            const resolutionType =
              selectedDataPoint.metricName === "Auto Resolved"
                ? "AutoResolved"
                : "ManualResolved";
            filters.push({
              name: "ResolutionType",
              values: [resolutionType],
            });
          }
          return filters;
        },
        transformData: (apiData, selectedFaultRow, selectedDataPoint) => {
          // For vehicles table, we group by batteryID, so the API returns ResolvedFaultCount
          // The resolution type is already filtered at the API level, so we just need to extract the count
          return apiData.map((item) => {
            const row = {
              id: item.xLabel || "",
              licensePlateNumber: item.xLabel || "",
              faultCount: 0,
              ...item.metaInfo,
            };

            // Extract fault count from metricsArray
            // When grouping by batteryID, the metric type is "ResolvedFaultCount"
            if (item.metricsArray && Array.isArray(item.metricsArray)) {
              item.metricsArray.forEach((metric) => {
                const metricType = (metric.type || "").toLowerCase();
                // For vehicles table, the API returns ResolvedFaultCount directly
                // since resolution type is already filtered in the API call
                if (metricType === "resolvedfaultcount" || metricType === "resolved fault count") {
                  row.faultCount = metric.value || 0;
                }
              });
            }

            return row;
          });
        },
        metaInfo: [
          "licensePlateNumber",
          "originCountry",
          "originState",
          "originCity",
          "originDepo",
          "pingStatus",
        ],
      },
    },
  },
  [CHART_TYPES.TRIGGERED_FAULT_BREAKDOWN]: {
    title: "Triggered Battery System Fault Breakdown",
    popupConfig: {
      getPopupTitle: (selectedDataPoint) => {
        const severityName = selectedDataPoint?.severityName || "Faults";
        const faultName = selectedDataPoint?.faultName || "";
        return `Drill Down: Triggered Battery System Fault Breakdown / ${faultName} / ${severityName}`;
      },
      summaryTable: {
        title: (selectedDataPoint) => {
          const severityName = selectedDataPoint?.severityName || "Alerts";
          return `${severityName} Alerts`;
        },
        tableColumnsConfig: {
          xLabel: {
            title: "Breakdown Dimension",
            dataIndex: "xLabel",
            key: "xLabel",
            width: 200,
            fixed: "left",
            defaultValue: "Not Set",
          },
          "1": {
            title: "Warning (L1) Faults ",
            dataIndex: "1",
            key: "1",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },

          "2": {
            title: "Warning (L2) Faults ",
            dataIndex: "2",
            key: "2",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },

          "3": {
            title: "High Severity (L3) Faults ",
            dataIndex: "3",
            key: "3",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          "4": {
            title: "High Severity (L4) Faults ",
            dataIndex: "4",
            key: "4",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          "5": {
            title: "Critical (L5) Faults ",
            dataIndex: "5",
            key: "5",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          vehicleCount: {
            title: "Vehicles Affected",
            dataIndex: "vehicleCount",
            key: "vehicleCount",
            width: 180,
            valueRenderingType: "number",
          },
          actions: {
            title: "Actions",
            headerLabel: "Actions",
            dataIndex: "actions",
            key: "actions",
            width: 120,
            fixed: "right",
            valueRenderingType: "details-link",
          },
        },
        tableColumnOrder: ["xLabel", "1", "2", "3", "4", "5", "vehicleCount", "actions"],
        apiSelect: "GeneratedFaultsCount",
        apiGroupBy: (groupBy) => {
          // Dynamic groupBy based on breakdown dimension
          // We need FaultSeverityName to get severity counts, then aggregate in transformData
          const baseGroupBy = ["errorDescription", "FaultSeverityName"];
          if (groupBy) {
            return [groupBy, ...baseGroupBy];
          }
          return baseGroupBy;
        },
        getApiFilters: (selectedDataPoint, baseFilters) => {
          const filters = [...baseFilters];
          // Don't filter by severityName for breakdown view - we want all severities
          // Only filter by faultName if provided
          if (selectedDataPoint?.faultName) {
            filters.push({
              name: "errorDescription",
              values: [selectedDataPoint.faultName],
            });
          }
          return filters;
        },
        transformData: (apiData, selectedDataPoint, vehiclesData, groupBy = "originCity") => {
          // Create a map of vehicles affected by breakdown dimension value
          const vehiclesAffectedMap = new Map();
          if (vehiclesData && Array.isArray(vehiclesData)) {
            vehiclesData.forEach((item) => {
              // Use breakdown dimension value as key (from metaInfo or xLabel)
              const breakdownKey = item.metaInfo?.[groupBy] || item.xLabel;
              if (breakdownKey) {
                let vehiclesAffected = 0;
                if (item.metricsArray && Array.isArray(item.metricsArray)) {
                  item.metricsArray.forEach((metric) => {
                    const metricType = (metric.type || "").toLowerCase();
                    if (metricType === "vehicles" || metricType === "vehicle" || metricType === "vehiclesaffected" || metricType === "vehicles affected") {
                      vehiclesAffected = metric.value || 0;
                    }
                    if (vehiclesAffected === 0 && item.metricsArray.length === 1) {
                      vehiclesAffected = metric.value || 0;
                    }
                  });
                }
                vehiclesAffectedMap.set(breakdownKey, vehiclesAffected);
              }
            });
          }

          // Map groupBy to dataIndex for extracting breakdown dimension value
          const breakdownDataIndexMap = {
            originState: "originState",
            originCity: "originCity",
            originDepo: "originDepo",
            configName: "configName",
            cellChemistry: "cellChemistry",
            hierarchy: "hierarchy",
            fleetName: "fleetName",
            fleetCategory: "fleetCategory",
          };
          const breakdownDataIndex = breakdownDataIndexMap[groupBy] || "originCity";

          // Aggregate data by breakdown dimension value
          const aggregatedData = new Map();

          apiData.forEach((item) => {
            // Extract breakdown dimension value from metaInfo or xLabel
            const breakdownValue = item.metaInfo?.[breakdownDataIndex] || item.xLabel || "";
            
            if (!breakdownValue) return;

            // Initialize or get existing aggregated row
            if (!aggregatedData.has(breakdownValue)) {
              aggregatedData.set(breakdownValue, {
                id: breakdownValue,
                [breakdownDataIndex]: breakdownValue,
                critical: 0,
                highSeverity: 0,
                warnings: 0,
                vehiclesAffected: 0,
              });
            }

            const row = aggregatedData.get(breakdownValue);

            // Extract severity counts from metricsArray
            if (item.metricsArray && Array.isArray(item.metricsArray)) {
              item.metricsArray.forEach((metric) => {
                const metricType = (metric.type || "").toLowerCase();
                const metricValue = metric.value || 0;
                
                // Map severity types to columns
                if (metricType === "critical" || metricType === "crit") {
                  row.critical += metricValue;
                } else if (metricType === "high severity" || metricType === "high" || metricType === "highseverity") {
                  row.highSeverity += metricValue;
                } else if (metricType === "warning" || metricType === "warn") {
                  row.warnings += metricValue;
                } else if (metricType === "generatedfaultscount" || metricType === "generated faults count") {
                  // If no specific severity type, check metaInfo for FaultSeverityName
                  const severityName = (item.metaInfo?.FaultSeverityName || "").toLowerCase();
                  if (severityName === "critical" || severityName === "crit") {
                    row.critical += metricValue;
                  } else if (severityName === "high severity" || severityName === "high") {
                    row.highSeverity += metricValue;
                  } else if (severityName === "warning" || severityName === "warn") {
                    row.warnings += metricValue;
                  }
                }
              });
            }

            // Update vehicles affected if available
            const vehiclesFromMap = vehiclesAffectedMap.get(breakdownValue);
            if (vehiclesFromMap !== undefined) {
              row.vehiclesAffected = vehiclesFromMap;
            } else if (item.metaInfo?.vehiclesAffected !== undefined) {
              row.vehiclesAffected = item.metaInfo.vehiclesAffected;
            }
          });

          // Convert map to array
          return Array.from(aggregatedData.values());
        },
      },
      vehiclesTable: {
        title: "Vehicles Affected",
        tableColumnsConfig: {
          licensePlateNumber: {
            title: "Bus Number",
            headerLabel: "Bus Number",
            dataIndex: "licensePlateNumber",
            key: "licensePlateNumber",
            width: 150,
            valueRenderingType: "device-id-link",
            fixed: "left",
          },
          "1": {
            title: "Warning (L1) Faults ",
            dataIndex: "1",
            key: "1",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },

          "2": {
            title: "Warning (L2) Faults ",
            dataIndex: "2",
            key: "2",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },

          "3": {
            title: "High Severity (L3) Faults ",
            dataIndex: "3",
            key: "3",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          "4": {
            title: "High Severity (L4) Faults ",
            dataIndex: "4",
            key: "4",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          "5": {
            title: "Critical (L5) Faults ",
            dataIndex: "5",
            key: "5",
            width: 100,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          originState: {
            title: "State",
            headerLabel: "State",
            dataIndex: "originState",
            key: "originState",
            width: 150,
            textFormat: "title",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          originCity: {
            title: "City",
            headerLabel: "City",
            dataIndex: "originCity",
            key: "originCity",
            width: 150,
            textFormat: "title",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          originDepo: {
            title: "Depot",
            headerLabel: "Depot",
            dataIndex: "originDepo",
            key: "originDepo",
            width: 200,
            textFormat: "title",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
        },
        tableColumnOrder: ["licensePlateNumber", "1", "2", "3", "4", "5", "originState", "originCity", "originDepo"],
        apiSelect: "GeneratedFaultsCount",
        apiGroupBy: ["batteryID"],
        getApiFilters: (selectedFaultRow, selectedDataPoint, baseFilters) => {
          const filters = [...baseFilters];
          if (selectedFaultRow?.faultName) {
            filters.push({
              name: "errorDescription",
              values: [selectedFaultRow.faultName],
            });
          }
          if (selectedDataPoint?.severityName) {
            filters.push({
              name: "FaultSeverityName",
              values: [selectedDataPoint.severityName],
            });
          }
          return filters;
        },
        transformData: (apiData, selectedFaultRow, selectedDataPoint) => {
          // For vehicles table, we group by batteryID, so the API returns GeneratedFaultsCount
          // The severity is already filtered at the API level, so we just need to extract the count
          return apiData.map((item) => {
            const row = {
              id: item.xLabel || "",
              licensePlateNumber: item.xLabel || "",
              faultCount: 0,
              ...item.metaInfo,
            };

            // Extract fault count from metricsArray
            // When grouping by batteryID, the metric type is "GeneratedFaultsCount"
            if (item.metricsArray && Array.isArray(item.metricsArray)) {
              item.metricsArray.forEach((metric) => {
                const metricType = (metric.type || "").toLowerCase();
                // For vehicles table, the API returns GeneratedFaultsCount directly
                // since severity is already filtered in the API call
                if (metricType === "generatedfaultscount" || metricType === "generated faults count") {
                  row.faultCount = metric.value || 0;
                }
              });
            }

            return row;
          });
        },
        metaInfo: [
          "licensePlateNumber",
          "originCountry",
          "originState",
          "originCity",
          "originDepo",
          "pingStatus",
        ],
      },
    },
  },
  [CHART_TYPES.RESOLUTION_DURATION_BREAKDOWN]: {
    title: "Battery System Fault Avg. Resolution Duration Breakdown",
    popupConfig: {
      getPopupTitle: (selectedDataPoint) => {
        const resolutionType = selectedDataPoint?.resolutionType || "Resolved";
        const faultName = selectedDataPoint?.faultName || "";
        return `Drill Down: Battery System Fault Avg. Resolution Duration Breakdown / ${faultName} / ${resolutionType}`;
      },
      summaryTable: {
        title: (selectedDataPoint) => {
          const resolutionType = selectedDataPoint?.resolutionType || "Resolved";
          return `${resolutionType} Alerts`;
        },
        tableColumnsConfig: {
          xLabel: {
            title: "Breakdown Dimension",
            dataIndex: "xLabel",
            key: "xLabel",
            width: 200,
            fixed: "left",
            defaultValue: "Not Set",
          },
          ResolvedFaultCount: {
            title: "Faults Resolved",
            dataIndex: "ResolvedFaultCount",
            key: "ResolvedFaultCount",
            width: 180,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          avgResolutionDuration: {
            title: "Avg. Resolution Duration",
            dataIndex: "avgResolutionDuration",
            key: "avgResolutionDuration",
            width: 220,
            valueRenderingType: "deltaTime 2",
            sortable: true,
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          minResolutionDuration: {
            title: "Min. Resolution Duration",
            dataIndex: "minResolutionDuration",
            key: "minResolutionDuration",
            width: 220,
            valueRenderingType: "deltaTime 2",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          maxResolutionDuration: {
            title: "Max. Resolution Duration",
            dataIndex: "maxResolutionDuration",
            key: "maxResolutionDuration",
            width: 220,
            valueRenderingType: "deltaTime 2",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          
          actions: {
            title: "Actions",
            headerLabel: "Actions",
            dataIndex: "actions",
            key: "actions",
            width: 120,
            fixed: "right",
            valueRenderingType: "details-link",
          },
        },
        tableColumnOrder: ["xLabel", "ResolvedFaultCount", "avgResolutionDuration", "minResolutionDuration", "maxResolutionDuration", "actions"],
        apiSelect: "ResolvedFaultCount",
        apiGroupBy: (groupBy) => {
          // Dynamic groupBy based on breakdown dimension
          const baseGroupBy = ["errorDescription", "ResolutionType"];
          if (groupBy) {
            return [groupBy, ...baseGroupBy];
          }
          return baseGroupBy;
        },
        getApiFilters: (selectedDataPoint, baseFilters) => {
          const filters = [...baseFilters];
          if (selectedDataPoint?.resolutionType) {
            const resolutionType =
              selectedDataPoint.resolutionType === "Auto Resolved"
                ? "AutoResolved"
                : "ManualResolved";
            filters.push({
              name: "ResolutionType",
              values: [resolutionType],
            });
          }
          if (selectedDataPoint?.faultName) {
            filters.push({
              name: "errorDescription",
              values: [selectedDataPoint.faultName],
            });
          }
          return filters;
        },
        transformData: (apiData, selectedDataPoint, vehiclesData, groupBy = "originCity") => {
          const resolutionType = (selectedDataPoint?.resolutionType || "").toLowerCase();
          
          // Create a map of vehicles affected by breakdown dimension value
          const vehiclesAffectedMap = new Map();
          if (vehiclesData && Array.isArray(vehiclesData)) {
            vehiclesData.forEach((item) => {
              // Use breakdown dimension value as key (from metaInfo or xLabel)
              const breakdownKey = item.metaInfo?.[groupBy] || item.xLabel;
              if (breakdownKey) {
                let vehiclesAffected = 0;
                if (item.metricsArray && Array.isArray(item.metricsArray)) {
                  if (item.metricsArray.length === 1) {
                    vehiclesAffected = item.metricsArray[0].value || 0;
                  } else {
                    item.metricsArray.forEach((metric) => {
                      const metricType = (metric.type || "").toLowerCase();
                      if (metricType === "vehicles" || metricType === "vehicle" || metricType === "vehiclesaffected" || metricType === "vehicles affected") {
                        vehiclesAffected = metric.value || 0;
                      }
                    });
                  }
                }
                vehiclesAffectedMap.set(breakdownKey, vehiclesAffected);
              }
            });
          }

          // Map groupBy to dataIndex for extracting breakdown dimension value
          const breakdownDataIndexMap = {
            originState: "originState",
            originCity: "originCity",
            originDepo: "originDepo",
            configName: "configName",
            cellChemistry: "cellChemistry",
            hierarchy: "hierarchy",
            fleetName: "fleetName",
            fleetCategory: "fleetCategory",
          };
          const breakdownDataIndex = breakdownDataIndexMap[groupBy] || "originCity";

          return apiData.map((item, index) => {
            // Extract breakdown dimension value from metaInfo or xLabel
            const breakdownValue = item.metaInfo?.[breakdownDataIndex] || item.xLabel || "";
            
            const row = {
              id: `breakdown-${index}`,
              [breakdownDataIndex]: breakdownValue,
              faultsResolved: 0,
              avgResolutionDuration: 0,
              minResolutionDuration: 0,
              maxResolutionDuration: 0,
              vehiclesAffected: 0,
            };

            // Extract metrics from metricsArray
            if (item.metricsArray && Array.isArray(item.metricsArray)) {
              item.metricsArray.forEach((metric) => {
                const metricType = (metric.type || "").toLowerCase();
                const metricValue = metric.value || 0;
                
                // Extract faults resolved count
                if (
                  (resolutionType === "auto resolved" || resolutionType === "auto") &&
                  (metricType === "resolvedfaultcount" || metricType === "autoresolved")
                ) {
                  row.faultsResolved = metricValue;
                } else if (
                  (resolutionType === "manually resolved" || resolutionType === "manual") &&
                  (metricType === "resolvedfaultcount" || metricType === "manualresolved" ||
                    metricType === "manual-resolved" || metricType === "manually resolved")
                ) {
                  row.faultsResolved = metricValue;
                }
                
                // Extract duration metrics
                if (metricType === "averageresolutiontime" || metricType === "average resolution time") {
                  row.avgResolutionDuration = metricValue;
                } else if (metricType === "mintimetoresolve" || metricType === "min time to resolve") {
                  row.minResolutionDuration = metricValue;
                } else if (metricType === "maxtimetoresolve" || metricType === "max time to resolve") {
                  row.maxResolutionDuration = metricValue;
                }
              });
            }

            // Get vehicles affected from the separate API call
            const vehiclesFromMap = vehiclesAffectedMap.get(breakdownValue);
            row.vehiclesAffected = vehiclesFromMap !== undefined 
              ? vehiclesFromMap 
              : (item.metaInfo?.vehiclesAffected !== undefined ? item.metaInfo.vehiclesAffected : 0);

            return row;
          });
        },
      },
      vehiclesTable: {
        title: "Vehicles Affected",
        tableColumnsConfig: {
          licensePlateNumber: {
            title: "Bus Number",
            headerLabel: "Bus Number",
            dataIndex: "licensePlateNumber",
            key: "licensePlateNumber",
            width: 150,
            valueRenderingType: "device-id-link",
            fixed: "left",
          },
          ResolvedFaultCount: {
            title: "Faults Resolved",
            dataIndex: "ResolvedFaultCount",
            key: "ResolvedFaultCount",
            width: 220,
            valueRenderingType: "number",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          originState: {
            title: "State",
            headerLabel: "State",
            dataIndex: "originState",
            key: "originState",
            width: 150,
            textFormat: "title",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          originCity: {
            title: "City",
            headerLabel: "City",
            dataIndex: "originCity",
            key: "originCity",
            width: 200,
            textFormat: "title",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
          originDepo: {
            title: "Depot",
            headerLabel: "Depot",
            dataIndex: "originDepo",
            key: "originDepo",
            width: 200,
            textFormat: "title",
            sorter: true,
            sortDirections: ["ascend", "descend", "ascend"],
          },
        },
        tableColumnOrder: ["licensePlateNumber", "ResolvedFaultCount", "originState", "originCity", "originDepo"],
        apiSelect: "ResolvedFaultCount",
        apiGroupBy: ["batteryID"],
        getApiFilters: (selectedFaultRow, selectedDataPoint, baseFilters) => {
          const filters = [...baseFilters];
          if (selectedFaultRow?.faultName) {
            filters.push({
              name: "errorDescription",
              values: [selectedFaultRow.faultName],
            });
          }
          if (selectedDataPoint?.resolutionType) {
            const resolutionType =
              selectedDataPoint.resolutionType === "Auto Resolved"
                ? "AutoResolved"
                : "ManualResolved";
            filters.push({
              name: "ResolutionType",
              values: [resolutionType],
            });
          }
          return filters;
        },
        transformData: (apiData, selectedFaultRow, selectedDataPoint) => {
          // For vehicles table, we group by batteryID, so the API returns ResolvedFaultCount
          // The resolution type is already filtered at the API level, so we just need to extract the count
          return apiData.map((item) => {
            const row = {
              id: item.xLabel || "",
              licensePlateNumber: item.xLabel || "",
              faultCount: 0,
              ...item.metaInfo,
            };

            // Extract fault count from metricsArray
            // When grouping by batteryID, the metric type is "ResolvedFaultCount"
            if (item.metricsArray && Array.isArray(item.metricsArray)) {
              item.metricsArray.forEach((metric) => {
                const metricType = (metric.type || "").toLowerCase();
                // For vehicles table, the API returns ResolvedFaultCount directly
                // since resolution type is already filtered in the API call
                if (metricType === "resolvedfaultcount" || metricType === "resolved fault count") {
                  row.faultCount = metric.value || 0;
                }
              });
            }

            return row;
          });
        },
        metaInfo: [
          "licensePlateNumber",
          "originCountry",
          "originState",
          "originCity",
          "originDepo",
          "pingStatus",
        ],
      },
    },
  },

  [CHART_TYPES.MOST_CRITICAL_VEHICLES]: {
    title: "Severity of Faults Triggered vs Vehicles Impacted",
    metaInfo: [
      "licensePlateNumber",
      "originCountry",
      "originState",
      "originCity",
      "originDepo",
      "pingStatus",
    ],
    tableColumnOrder: [
      "licensePlateNumber",
      "critical",
      "high severity",
      "warning",
      "originCountry",
      "originState",
      "originCity",
      "originDepo",
    ],
    tableColumnsConfig: {
      licensePlateNumber: {
        title: "Bus Number",
        headerLabel: "Bus Number",
        dataIndex: "licensePlateNumber",
        key: "licensePlateNumber",
        width: "auto",
        valueRenderingType: "device-id-link",
        fixed: "left",
      },
      critical: {
        title: "Critical Faults",
        headerLabel: "Critical Faults",
        dataIndex: "critical",
        key: "critical",
        valueRenderingType: "number",
        width: 150,
      },
      "high severity": {
        title: "High Severity Faults",
        headerLabel: "High Severity Faults",
        dataIndex: "high severity",
        key: "high severity",
        textFormat: "title",
        valueRenderingType: "number",
        width: 180,
      },
      warning: {
        title: "Warnings",
        headerLabel: "Warnings",
        dataIndex: "warning",
        key: "warning",
        width: 120,
        valueRenderingType: "number",
      },
      originCountry: {
        title: "Country",
        headerLabel: "Country",
        dataIndex: "originCountry",
        key: "originCountry",
        textFormat: "title",
        width: 150,
      },
      originState: {
        title: "State",
        headerLabel: "State",
        dataIndex: "originState",
        key: "originState",
        textFormat: "title",
        width: 150,
      },
      originCity: {
        title: "City",
        headerLabel: "City",
        dataIndex: "originCity",
        key: "originCity",
        textFormat: "title",
        width: 150,
      },
      originDepo: {
        title: "Depot",
        headerLabel: "Depot",
        dataIndex: "originDepo",
        key: "originDepo",
        textFormat: "title",
        width: 200,
      },
    }
  },
};
